import { useCallback, useEffect } from 'react';

import WebFont from 'webfontloader';

import { useThemeConfig } from '@/lib/hooks/useThemeConfig';
import { APP_CONFIG_CONSTANTS } from '../../constants/appConfig.constants';

export const useThemeManager = () => {
  const {
    lightThemeColors,
    darkThemeColors,
    themeMode,
    setThemeMode,
    settings,
  } = useThemeConfig();

  const applyThemeColors = useCallback((colors: Record<string, string>) => {
    const root = document.documentElement;
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }, []);

  // Apply theme mode and colors
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');

    if (themeMode === 'auto') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
      // In 'auto' mode, follow the system preference visually without
      // persisting a derived mode into the store. This preserves the
      // user's explicit choice if they later select light/dark.
      const colors =
        systemTheme === 'dark' ? darkThemeColors : lightThemeColors;
      applyThemeColors(colors);
    } else {
      root.classList.add(themeMode);
      const colors = themeMode === 'dark' ? darkThemeColors : lightThemeColors;
      applyThemeColors(colors);
    }
  }, [
    themeMode,
    darkThemeColors,
    lightThemeColors,
    setThemeMode,
    applyThemeColors,
  ]);

  // Load custom font
  useEffect(() => {
    const root = document.documentElement;
    const font =
      (settings?.ui?.theming?.font as string) ||
      APP_CONFIG_CONSTANTS.DEFAULT_FONT;

    WebFont.load({
      google: { families: [font] },
      active() {
        root.style.setProperty('font-family', `${font}, sans-serif`);
      },
      inactive() {
        console.warn(`Failed to load font: ${font}, using fallback`);
        root.style.setProperty(
          'font-family',
          `${APP_CONFIG_CONSTANTS.DEFAULT_FONT}, sans-serif`
        );
      },
    });
  }, [settings?.ui?.theming?.font]);
};
